<template>
  <LvDropdownTreeCheckboxSelect
    remindText="紅字為無權限"
    :value="value"
    :title="title"
    :menu="menuItems"
    :required="required"
    :rules="rules"
    :placeholder="placeholder"
    :disabled="disabled"
    :noErrorText="noErrorText"
    :hasCancelIcon="hasCancelIcon"
    :topNodeDisableList="topNodeDisableList"
    @input="$emit('input', $event)"
  />
</template>

<script>
import H_systemClassMenu from '@/helpers/systemClassMenu';

export default {
  name: 'LvDropdownMultiSystemClasses',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    selectValue: {
      type: Object,
      default: () => ({
        system_classes: [],
        system_subclasses: [],
        factory_system_classes: [],
        factory_system_subclasses: [],
      }),
    },
    title: {
      type: String,
      default: '領域',
    },
    required: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: String,
      default: '',
    },
    propsMenuItems: {
      type: Array,
    },
    placeholder: {
      type: String,
      default: '選擇',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasCancelIcon: {
      type: Boolean,
      default: false,
    },
    noErrorText: {
      type: Boolean,
      default: false,
    },
    topNodeDisableList: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      menuItems: [],
    };
  },
  async mounted() {
    this.menuItems = await this.$_getClassMenu();
    this.$_initValue();
  },
  methods: {
    async $_getClassMenu() {
      if (this.propsMenuItems !== undefined) {
        return this.propsMenuItems;
      }
      return await H_systemClassMenu.fetchMultiSystemClassMenu({
        systemClasses: this.$store.state.model.systemClasses,
      });
    },
    $_buildLeafIdsMap(nodes) {
      const leafMap = {};
      const walk = node => {
        if (!node.items || node.items.length === 0) {
          const leafIds = [node.id];
          leafMap[node.id] = leafIds;
          return leafIds;
        }
        const leafIds = [];
        const children = node.items;
        for (let c = 0; c < children.length; c += 1) {
          const childLeaves = walk(children[c]);
          for (let i = 0; i < childLeaves.length; i += 1) {
            leafIds.push(childLeaves[i]);
          }
        }
        leafMap[node.id] = leafIds;
        return leafIds;
      };
      (nodes || []).forEach(node => walk(node));
      return leafMap;
    },
    $_buildSelectedTreeFromLeaves(menu, selectedLeafSet, leafIdsMap) {
      const selectedTree = [];
      (menu || []).forEach(node => {
        const leafIds = leafIdsMap[node.id] || [];
        const hasSelection = leafIds.some(leafId => selectedLeafSet.has(leafId));
        if (!hasSelection) return;
        const children = node.items || [];
        if (children.length === 0) {
          selectedTree.push({ id: node.id, items: [] });
          return;
        }
        const selectedChildren = this.$_buildSelectedTreeFromLeaves(
          children,
          selectedLeafSet,
          leafIdsMap,
        );
        selectedTree.push({ id: node.id, items: selectedChildren });
      });
      return selectedTree;
    },
    $_mergePathStackIntoTree(selectedTree, pathStack) {
      let level = selectedTree;
      for (let i = 0; i < pathStack.length; i += 1) {
        const menuNode = pathStack[i];
        const isLast = i === pathStack.length - 1;
        let existing = level.find(n => n.id === menuNode.id);
        if (!existing) {
          existing = { id: menuNode.id, items: [] };
          level.push(existing);
        }
        if (isLast) {
          existing.items = [];
          return;
        }
        if (!existing.items) {
          existing.items = [];
        }
        level = existing.items;
      }
    },
    $_buildMenuParentIndex(nodes) {
      const idToNode = {};
      const parentOfId = {};
      const walk = (list, parentNode) => {
        const arr = list || [];
        for (let i = 0; i < arr.length; i += 1) {
          const node = arr[i];
          idToNode[node.id] = node;
          if (parentNode) {
            parentOfId[node.id] = parentNode.id;
          }
          walk(node.items || [], node);
        }
      };
      walk(nodes || [], null);
      return { idToNode, parentOfId };
    },
    $_pathNodesFromRoot(menuIndex, targetId) {
      const { idToNode, parentOfId } = menuIndex;
      let cur = idToNode[targetId];
      if (!cur) {
        return null;
      }
      const rev = [];
      while (cur) {
        rev.push(cur);
        const pId = parentOfId[cur.id];
        if (pId === undefined) {
          break;
        }
        cur = idToNode[pId];
      }
      return rev.reverse();
    },
    $_initValue() {
      if (this.value?.length > 0) {
        return;
      }
      const {
        system_classes = [],
        system_subclasses = [],
        factory_system_classes = [],
        factory_system_subclasses = [],
      } = this.selectValue;
      if (
        !system_classes.length &&
        !system_subclasses.length &&
        !factory_system_classes.length &&
        !factory_system_subclasses.length
      ) {
        return;
      }
      const menu = this.menuItems;
      if (!menu.length) {
        return;
      }
      const getIds = dataList => dataList.map(item => item.id);
      const systemClassIds = getIds(system_classes);
      const systemSubclassIds = getIds(system_subclasses);
      const factorySystemClassIds = getIds(factory_system_classes);
      const factorySystemSubclassIds = getIds(factory_system_subclasses);

      const leafIdsMap = this.$_buildLeafIdsMap(menu);
      const selectedLeafSet = new Set(systemSubclassIds);
      factorySystemSubclassIds.forEach(id => selectedLeafSet.add(id));
      const systemSubclassSet = new Set(systemSubclassIds);
      const factorySubclassSet = new Set(factorySystemSubclassIds);
      const systemParentOnlyRowIds = systemClassIds.filter(parentId => {
        const leaves = leafIdsMap[parentId] || [];
        return !leaves.some(lid => systemSubclassSet.has(lid));
      });
      const factoryParentOnlyRowIds = factorySystemClassIds.filter(parentId => {
        const leaves = leafIdsMap[parentId] || [];
        return !leaves.some(lid => factorySubclassSet.has(lid));
      });

      const treeFromLeaves = this.$_buildSelectedTreeFromLeaves(menu, selectedLeafSet, leafIdsMap);
      const parentOnlyOrdered = [...factoryParentOnlyRowIds, ...systemParentOnlyRowIds];
      if (parentOnlyOrdered.length) {
        const menuIndex = this.$_buildMenuParentIndex(menu);
        parentOnlyOrdered.forEach(pid => {
          const path = this.$_pathNodesFromRoot(menuIndex, pid);
          if (path && path.length >= 2) {
            this.$_mergePathStackIntoTree(treeFromLeaves, path);
          }
        });
      }
      this.$emit('init-value', treeFromLeaves);
    },
  },
};
</script>
